import React, { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, node, number, oneOfType, shape, string } from 'prop-types';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { NoAvailableCard } from 'common';
import palette from '../../../../../../library/theme/palette';

const parseTerminalShortName = shortName => {
  const [locationId, ...rest] = (shortName || '').trim().split(' ');
  return {
    locationId: locationId || '--',
    locationName: rest.length ? rest.join(' ') : shortName || '--',
  };
};

const getTerminalLocation = terminal => {
  const parsed = parseTerminalShortName(terminal.terminalShortName);
  const locationId = terminal.terminalIdName ?? parsed.locationId;
  const shortName = (terminal.terminalShortName || '').trim();
  const idPrefix = locationId ? `${locationId} ` : '';

  return {
    locationId,
    locationName:
      terminal.terminalIdName && shortName.startsWith(idPrefix)
        ? shortName.slice(idPrefix.length) || '--'
        : shortName || parsed.locationName,
  };
};

// Numeric-aware sort so '9' sorts before '10' and '217'.
const compareLocationIds = (a, b) => {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return numA - numB;
  }
  return String(a).localeCompare(String(b));
};

const renderTooltipCell = ({ value }) => (
  <Tooltip arrow placement="bottom" title={value || ''}>
    <span
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </Tooltip>
);

// Same status bubble treatment as components/TerminalsAccordion.jsx.
const renderLoadingStatusIcon = status =>
  status ? (
    <Box
      aria-label="Loading enabled"
      role="img"
      sx={{
        backgroundColor: 'success.dark',
        borderRadius: '50%',
        height: 14,
        mr: 0.5,
        position: 'relative',
        width: 14,
        zIndex: 0,
      }}
    >
      <CheckCircleRoundedIcon
        aria-hidden="true"
        sx={{
          color: 'success.light',
          height: 20,
          left: -3,
          position: 'absolute',
          top: -3,
          width: 20,
        }}
      />
    </Box>
  ) : (
    <Box
      aria-label="Loading disabled"
      role="img"
      sx={{
        backgroundColor: 'error.main',
        borderRadius: '50%',
        height: 14,
        mr: 0.5,
        position: 'relative',
        width: 14,
        zIndex: 0,
      }}
    >
      <ErrorIcon
        aria-hidden="true"
        sx={{
          color: 'error.light',
          height: 20,
          left: -3,
          position: 'absolute',
          top: -3,
          width: 20,
        }}
      />
    </Box>
  );

const LocationsGrid = ({
  aboveGridContent,
  belowQuickFilterContent,
  darkHeader,
  extraColumns,
  framed,
  gridHeight,
  gridProps,
  hideColumns,
  leadingColumns,
  newTerminalIds,
  panelTitle,
  quickFilterAriaLabel,
  quickFilterLabel,
  quickFilterOnly,
  quickFilterPlacement,
  quickFilterPlaceholder,
  showSelectionHeaderCheckbox,
  showTotalRows,
  terminals,
  topRightContent,
}) => {
  const [quickFilterInput, setQuickFilterInput] = useState('');
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(value => value.trim())
          .filter(Boolean)
      );
    }, 200);
    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  const rows = useMemo(
    () =>
      [...terminals]
        .sort((left, right) => compareLocationIds(left.terminalId, right.terminalId))
        .map(terminal => ({
          id: terminal.terminalId,
          isNew: newTerminalIds.includes(terminal.terminalId),
          terminalLoadingStatus: terminal.terminalLoadingStatus,
          ...getTerminalLocation(terminal),
        })),
    [newTerminalIds, terminals]
  );

  const columns = useMemo(
    () =>
      [
        ...leadingColumns,
        {
          align: 'left',
          field: 'locationId',
          flex: 0.35,
          headerAlign: 'left',
          headerName: 'ID',
          minWidth: 80,
          renderCell: renderTooltipCell,
          resizable: true,
          sortComparator: compareLocationIds,
        },
        {
          align: 'left',
          field: 'locationName',
          flex: 1,
          headerAlign: 'left',
          headerName: 'Location Name',
          minWidth: 160,
          renderCell: ({ row, value }) => (
            <Box
              sx={{ alignItems: 'center', display: 'flex', gap: 1, maxWidth: '100%', minWidth: 0 }}
            >
              {renderTooltipCell({ value })}
              {row.isNew && (
                <Chip
                  label="New"
                  size="small"
                  sx={{
                    backgroundColor: '#FCE7D6',
                    borderRadius: '3px',
                    color: '#E86A00',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    height: 24,
                    textTransform: 'uppercase',
                  }}
                />
              )}
            </Box>
          ),
          resizable: true,
        },
        {
          align: 'center',
          field: 'terminalLoadingStatus',
          // Loading Status is not a searchable quick-filter field.
          getApplyQuickFilterFn: () => null,
          headerAlign: 'center',
          headerName: 'Loading Status',
          minWidth: 155,
          renderCell: ({ row }) => renderLoadingStatusIcon(row.terminalLoadingStatus),
          resizable: true,
          // Ascending shows loading-enabled (Y) locations before disabled (N).
          sortComparator: (a, b) => Number(b) - Number(a),
          width: 155,
        },
        ...extraColumns,
      ].filter(column => !hideColumns.includes(column.field)),
    [extraColumns, hideColumns, leadingColumns]
  );

  const filterModel = useMemo(() => ({ items: [], quickFilterValues }), [quickFilterValues]);
  const quickFilter = (
    <TextField
      fullWidth
      onChange={event => setQuickFilterInput(event.target.value)}
      placeholder={quickFilterPlaceholder}
      size="small"
      slotProps={{
        input: {
          'aria-label': quickFilterAriaLabel || quickFilterLabel,
          endAdornment: quickFilterInput ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                edge="end"
                onClick={() => setQuickFilterInput('')}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      value={quickFilterInput}
      variant="outlined"
    />
  );

  return (
    <Box sx={{ width: '100%' }}>
      {quickFilterPlacement === 'top' && (
        <Box
          sx={{
            alignItems: { lg: 'center', xs: 'stretch' },
            backgroundColor: 'common.white',
            display: 'flex',
            flexDirection: { lg: 'row', xs: 'column' },
            gap: 1.25,
            pb: quickFilterOnly ? 0 : 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{quickFilter}</Box>
          {topRightContent}
        </Box>
      )}
      {belowQuickFilterContent}
      {aboveGridContent}
      {!quickFilterOnly && (
        <Box
          sx={
            framed
              ? {
                  backgroundColor: 'common.white',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }
              : {
                  backgroundColor: 'common.white',
                  overflow: 'hidden',
                }
          }
        >
          {quickFilterPlacement === 'header' && (
            <Box
              sx={{
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                color: 'text.primary',
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography color="inherit" fontWeight={700} variant="body2">
                {panelTitle}
              </Typography>
              <Box sx={{ maxWidth: 240, width: '100%' }}>{quickFilter}</Box>
            </Box>
          )}
          <Box sx={{ height: gridHeight || 'auto' }}>
            <DataGridPremium
              autoHeight={!gridHeight}
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableRowSelectionOnClick
              filterModel={filterModel}
              getRowClassName={({ row }) => (row.isNew ? 'new-terminal-row' : '')}
              hideFooter
              onSortModelChange={setSortModel}
              rows={rows}
              slotProps={{
                baseCheckbox: {
                  size: 'small',
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <NoAvailableCard
                    suggestions="Try adjusting your search or filter options."
                    type="locations"
                  />
                ),
              }}
              sortingOrder={['asc', 'desc', null]}
              sortModel={sortModel}
              sx={{
                '& .MuiDataGrid-cell': {
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                ...(!showSelectionHeaderCheckbox && {
                  '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                    display: 'none',
                  },
                }),
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: darkHeader ? palette._next.surface.white : 'text.primary',
                  fontWeight: 700,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                },
                '& .MuiDataGrid-filler': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                  color: darkHeader ? palette._next.surface.white : 'text.secondary',
                },
                '& .MuiDataGrid-row': {
                  backgroundColor: 'common.white',
                },
                '& .MuiDataGrid-row.new-terminal-row .MuiDataGrid-cell': {
                  backgroundColor: '#FFF7F1',
                },
                '& .MuiDataGrid-scrollbarFiller': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                border: 0,
              }}
              {...gridProps}
            />
          </Box>
          {showTotalRows && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 1 }}>
              <Typography color="text.primary" variant="body2">
                {`Total Rows: ${rows.length}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

LocationsGrid.propTypes = {
  aboveGridContent: node,
  belowQuickFilterContent: node,
  darkHeader: bool,
  extraColumns: arrayOf(shape({})),
  framed: bool,
  gridHeight: oneOfType([number, string]),
  gridProps: shape({}),
  hideColumns: arrayOf(string),
  leadingColumns: arrayOf(shape({})),
  newTerminalIds: arrayOf(oneOfType([number, string])),
  panelTitle: string,
  quickFilterAriaLabel: string,
  quickFilterLabel: string,
  quickFilterOnly: bool,
  quickFilterPlaceholder: string,
  quickFilterPlacement: string,
  showSelectionHeaderCheckbox: bool,
  showTotalRows: bool,
  terminals: arrayOf(
    shape({
      terminalId: oneOfType([number, string]),
      terminalIdName: oneOfType([number, string]),
      terminalLoadingStatus: bool,
      terminalShortName: string,
    })
  ),
  topRightContent: node,
};

LocationsGrid.defaultProps = {
  aboveGridContent: null,
  belowQuickFilterContent: null,
  darkHeader: false,
  extraColumns: [],
  framed: true,
  gridHeight: null,
  gridProps: {},
  hideColumns: [],
  leadingColumns: [],
  newTerminalIds: [],
  panelTitle: '',
  quickFilterAriaLabel: '',
  quickFilterLabel: 'Filter Locations',
  quickFilterOnly: false,
  quickFilterPlaceholder: 'Search by ID or Location Name',
  quickFilterPlacement: 'top',
  showSelectionHeaderCheckbox: false,
  showTotalRows: false,
  terminals: [],
  topRightContent: null,
};

export default LocationsGrid;
